import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  User, Building, Phone, MapPin, Stethoscope, Shield, 
  FileText, Save, CheckCircle, AlertCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Sidebar } from "./Dashboard";
import PromoCodeCard from "../components/PromoCodeCard";
import RoleSettingsCard from "../components/RoleSettingsCard";
import { useAuth, API } from "../App";
import { toast } from "sonner";

const Profile = () => {
  const location = useLocation();
  const { user, getAuthHeaders } = useAuth();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [specialties, setSpecialties] = useState([]);
  const [insuranceOptions, setInsuranceOptions] = useState([]);

  const [formData, setFormData] = useState({
    display_name: "",
    specialty: "",
    sub_specialty: "",
    practice_name: "",
    npi_number: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    insurance_accepted: [],
    case_types: [],
    bio: "",
    accepting_referrals: true
  });

  const US_STATES = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
    "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
    "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
    "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
    "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
  ];

  const fetchProfileData = useCallback(async () => {
    try {
      const [profileRes, specialtiesRes, insuranceRes] = await Promise.all([
        axios.get(`${API}/profile`, {
          headers: getAuthHeaders(),
          withCredentials: true
        }),
        axios.get(`${API}/options/specialties`, {
          headers: getAuthHeaders(),
          withCredentials: true
        }),
        axios.get(`${API}/options/insurance`, {
          headers: getAuthHeaders(),
          withCredentials: true
        })
      ]);
      
      setProfile(profileRes.data);
      setSpecialties(specialtiesRes.data.specialties);
      setInsuranceOptions(insuranceRes.data.insurance);
      
      setFormData({
        display_name: profileRes.data.name || "",
        specialty: profileRes.data.specialty || "",
        sub_specialty: profileRes.data.sub_specialty || "",
        practice_name: profileRes.data.practice_name || "",
        npi_number: profileRes.data.npi_number || "",
        phone: profileRes.data.phone || "",
        address: profileRes.data.address || "",
        city: profileRes.data.city || "",
        state: profileRes.data.state || "",
        zip_code: profileRes.data.zip_code || "",
        insurance_accepted: profileRes.data.insurance_accepted || [],
        case_types: profileRes.data.case_types || [],
        bio: profileRes.data.bio || "",
        accepting_referrals: profileRes.data.accepting_referrals ?? true
      });
    } catch (error) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Update name if changed
      if (formData.display_name && formData.display_name !== user?.name) {
        await axios.put(`${API}/auth/update-name`, { name: formData.display_name }, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
      }

      const response = await axios.put(`${API}/profile`, formData, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setProfile(response.data);
      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleInsuranceToggle = (insurance) => {
    setFormData(prev => ({
      ...prev,
      insurance_accepted: prev.insurance_accepted.includes(insurance)
        ? prev.insurance_accepted.filter(i => i !== insurance)
        : [...prev.insurance_accepted, insurance]
    }));
  };

  const isProfileComplete = () => {
    const required = ['specialty', 'practice_name', 'phone', 'city', 'state'];
    return required.every(field => formData[field]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 lg:pl-64">
        <Sidebar currentPath={location.pathname} />
        <main className="pt-20 lg:pt-8 p-4 lg:p-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl border border-slate-200 p-8 animate-pulse">
              <div className="flex items-center gap-6 mb-8">
                <div className="w-24 h-24 bg-slate-200 rounded-full"></div>
                <div>
                  <div className="h-6 bg-slate-200 rounded w-48 mb-2"></div>
                  <div className="h-4 bg-slate-200 rounded w-32"></div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 lg:pl-64">
      <Sidebar currentPath={location.pathname} />
      
      <main className="pt-20 lg:pt-8 p-4 lg:p-8" data-testid="profile-main">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Your Profile
            </h1>
            <p className="text-slate-600">Manage your physician profile and preferences</p>
          </div>

          {/* Profile Completion Alert */}
          {!isProfileComplete() && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3"
            >
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-800 font-medium">Complete your profile to appear in the directory</p>
                <p className="text-amber-700 text-sm">Fill in your specialty, practice name, phone, and location to start receiving referrals.</p>
              </div>
            </motion.div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Profile Header Card */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <Avatar className="w-24 h-24">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-primary/10 text-primary text-2xl">
                    {user?.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="text-center sm:text-left flex-1">
                  <div className="mb-1">
                    <Label className="text-xs text-slate-400">Display Name</Label>
                    <Input
                      value={formData.display_name}
                      onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                      className="text-xl font-semibold mt-0.5 h-9"
                      placeholder="Your full name"
                      data-testid="profile-name-input"
                    />
                  </div>
                  <p className="text-slate-500">{user?.email}</p>
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-2">
                    <Badge className={profile?.membership_tier === 'basic' ? 'bg-slate-100 text-slate-700' : 'bg-primary/10 text-primary'}>
                      {profile?.membership_tier?.charAt(0).toUpperCase() + profile?.membership_tier?.slice(1)} Member
                    </Badge>
                    {profile?.profile_complete && (
                      <Badge className="badge-accepted">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Profile Complete
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Label htmlFor="accepting" className="text-sm text-slate-600">Accepting Referrals</Label>
                  <Switch
                    id="accepting"
                    checked={formData.accepting_referrals}
                    onCheckedChange={(checked) => setFormData({...formData, accepting_referrals: checked})}
                  />
                </div>
              </div>
            </div>

            {/* Professional Information */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Stethoscope className="w-5 h-5 text-primary" />
                Professional Information
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="specialty">Specialty *</Label>
                  <Select 
                    value={formData.specialty} 
                    onValueChange={(v) => setFormData({...formData, specialty: v})}
                  >
                    <SelectTrigger id="specialty" data-testid="specialty-select">
                      <SelectValue placeholder="Select specialty" />
                    </SelectTrigger>
                    <SelectContent>
                      {specialties.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="sub_specialty">Sub-Specialty</Label>
                  <Input
                    id="sub_specialty"
                    value={formData.sub_specialty}
                    onChange={(e) => setFormData({...formData, sub_specialty: e.target.value})}
                    placeholder="e.g., Interventional Cardiology"
                    data-testid="sub-specialty-input"
                  />
                </div>

                <div>
                  <Label htmlFor="practice_name">Practice Name *</Label>
                  <Input
                    id="practice_name"
                    value={formData.practice_name}
                    onChange={(e) => setFormData({...formData, practice_name: e.target.value})}
                    placeholder="e.g., Heart & Vascular Center"
                    required
                    data-testid="practice-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="npi_number">NPI Number</Label>
                  <Input
                    id="npi_number"
                    value={formData.npi_number}
                    onChange={(e) => setFormData({...formData, npi_number: e.target.value})}
                    placeholder="10-digit NPI"
                    data-testid="npi-input"
                  />
                </div>
              </div>
            </div>

            {/* Contact & Location */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                Contact & Location
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="(555) 123-4567"
                    required
                    data-testid="phone-input"
                  />
                </div>

                <div>
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="123 Medical Center Dr"
                    data-testid="address-input"
                  />
                </div>

                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    placeholder="Your city"
                    required
                    data-testid="city-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="state">State *</Label>
                    <Select 
                      value={formData.state} 
                      onValueChange={(v) => setFormData({...formData, state: v})}
                    >
                      <SelectTrigger id="state" data-testid="state-select">
                        <SelectValue placeholder="State" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="zip_code">ZIP Code</Label>
                    <Input
                      id="zip_code"
                      value={formData.zip_code}
                      onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                      placeholder="75001"
                      data-testid="zip-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Insurance Accepted */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                Insurance Accepted
              </h3>
              
              <div className="flex flex-wrap gap-2">
                {insuranceOptions.map((insurance) => (
                  <button
                    key={insurance}
                    type="button"
                    onClick={() => handleInsuranceToggle(insurance)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      formData.insurance_accepted.includes(insurance)
                        ? 'bg-primary text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                    data-testid={`insurance-${insurance}`}
                  >
                    {formData.insurance_accepted.includes(insurance) && (
                      <CheckCircle className="w-4 h-4 inline mr-1" />
                    )}
                    {insurance}
                  </button>
                ))}
              </div>
            </div>

            {/* Bio */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Bio
              </h3>
              
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({...formData, bio: e.target.value})}
                placeholder="Tell other physicians about your practice, specializations, and what makes you a great referral partner..."
                className="w-full min-h-[120px] px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                data-testid="bio-textarea"
              />
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button 
                type="submit" 
                className="bg-primary text-white hover:bg-primary/90 rounded-full px-8"
                disabled={saving}
                data-testid="save-profile-btn"
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Save className="w-4 h-4" />
                    Save Profile
                  </span>
                )}
              </Button>
            </div>
          </form>

          {/* Role Settings */}
          <div className="mt-6">
            <RoleSettingsCard />
          </div>

          {/* Promo Code Section */}
          <div className="mt-6">
            <PromoCodeCard variant="card" />
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile;
